import { Injectable } from '@angular/core';
import {FidjiNatifLibraryService, IMDocument, IMPublication} from '@immanens-com/fidji-api-module';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {DocumentControllerService} from '../document/document-controller.service';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {Buffer} from 'buffer';
import {map} from 'rxjs/operators';

declare const window;

@Injectable()
export class LibraryControllerService {

  private localDocumentsSubject = new BehaviorSubject<IMDocument[]>([]);
  public localDocumentsChange = this.localDocumentsSubject.asObservable();

  private localPublicationsSubject = new BehaviorSubject<IMPublication[]>([]);
  public localPublicationsChange = this.localPublicationsSubject.asObservable();

  private cloudDocumentsSubject = new BehaviorSubject<IMDocument[]>([]);
  public cloudDocumentsChange = this.cloudDocumentsSubject.asObservable();

  constructor(private library: FidjiNatifLibraryService,
              private documentCtrl: DocumentControllerService) {

    this.fetchLibraries();

    this.loadDocuments();
    this.subscribeToLocalPublications();
    this.subscribeToCloudDocuments();

  }

  public fetchLibraries() {
    this.library.fetchLibraries();
  }

  private loadDocuments(): void {
    this.library.localLibrary.subscribe((localDocuments) => {
      if (localDocuments != null) {
        this.localDocumentsSubject.next(localDocuments.map((doc) => {
          if (window.webkit != null && window.webkit !== undefined) { // We are on ios
            doc.pub_title = new Buffer(doc.pub_title, 'ascii').toString('utf-8');
          }
          return doc;
        }));
      }
    });
  }

  private subscribeToLocalPublications() {
    combineLatest(this.localDocumentsChange, this.documentCtrl.userDownloadedDocumentsChange,
      (localDocuments, downloadedDocuments) => {
        localDocuments = localDocuments || []; // Avoid null
        downloadedDocuments = downloadedDocuments || [];

        return localDocuments.concat(downloadedDocuments);

      }).subscribe((docs) => {

      let localPublications = [];
      const countDocsPerPubs = [];

      docs.forEach(doc => {

        const docAlreadyHaveAPublication = localPublications.findIndex(
          (pub) => pub.dly_pub_id === doc.dly_pub_id
        ) !== -1;

        if (docAlreadyHaveAPublication) {
          if (countDocsPerPubs[doc.dly_pub_id] !== undefined) {
            countDocsPerPubs[doc.dly_pub_id]++;
          } else {
            countDocsPerPubs[doc.dly_pub_id] = 1;
          }
        } else {
          countDocsPerPubs[doc.dly_pub_id] = 1;
        }

        if (localPublications == null || docAlreadyHaveAPublication) { return; } // Avoid duplicate

        return localPublications.push({ // Create a false publication for each documents
          dly_pub_id: doc.dly_pub_id,
          title: doc.pub_title,
          doc_data: doc,
          logistic_pub_id: doc.logistic_pub_id,
          logistic_code: doc.logistic_code
        } as IMPublication);

      });

      localPublications.map(pub => {
        pub['doc_count'] = countDocsPerPubs[pub.dly_pub_id];
      });

      localPublications = localPublications.sort((pub1, pub2) => pub1.title > pub2.title ? 1 : -1);

      return this.localPublicationsSubject.next(localPublications);
    });
  }

  subscribeToCloudDocuments() {
    combineLatest(
      this.documentCtrl.userDownloadedDocumentsChange,
      this.localDocumentsChange
      , (downloadedDocs, localDocs) => {

        if (downloadedDocs == null || downloadedDocs.length === 0) { return []; }
        if (localDocs == null || localDocs.length === 0) { return downloadedDocs; }

        const cloudDocuments = downloadedDocs.filter((doc) => { // Filter downloaded document with local docs

          const presentInLocalDocs = localDocs.findIndex((localDoc) => {
            return localDoc.dly_doc_id === doc.dly_doc_id;
          }) !== -1;

          return !presentInLocalDocs; // If document is present in local doc it is no more a cloud document
        });

        return cloudDocuments;

      }).subscribe((cloudDocuments) => {
      this.cloudDocumentsSubject.next(cloudDocuments);
    });
  }

  public localPublicationById(dly_pub_id: string): Observable<IMPublication> {
    return this.localPublicationsSubject.pipe(
      map(pubs => pubs.find((pub) => pub.dly_pub_id === dly_pub_id))
    );
  }

  public localDocumentForPublication(dly_pub_id: string): Observable<IMDocument[]> {
    return this.localDocumentsSubject.map(docList => {
      return docList
        .filter((doc) => doc.dly_pub_id === dly_pub_id)
        .sort((doc1, doc2) => doc1.doc_title > doc2.doc_title ? 1 : -1);
    });
  }


  public cloudDocumentForPublication(dly_pub_id: string): Observable<IMDocument[]> {
    return this.cloudDocumentsChange.map((docs) => {
      if (docs == null || docs.length === 0) { return []; }
      return docs.filter(doc => doc.dly_pub_id === dly_pub_id);
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/core/services/library/library-controller.service.ts